import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContextLazy';
import { supabase } from '../../../lib/supabase-auth';
import {
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  ArrowRightIcon,
  CalendarDaysIcon,
  ExclamationCircleIcon,
  FolderOpenIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';

interface Party {
  id: string;
  name: string;
  primary_email: string | null;
}

interface ClientProject {
  id: string;
  project_number: string | null;
  name: string;
  status: string;
  current_phase: number;
  total_phases: number;
  tasks_total: number;
  tasks_completed: number;
  docs_required: number;
  docs_received: number;
  target_completion_date: string | null;
  engaged_date: string | null;
}

interface ClientTask {
  id: string;
  project_id: string;
  title: string;
  description: string | null;
  due_date: string | null;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  status: string;
  project_name?: string;
}

export const ClientPortalDashboard: React.FC = () => {
  const { userProfile, user } = useAuth();
  const navigate = useNavigate();

  const [clientParty, setClientParty] = useState<Party | null>(null);
  const [projects, setProjects] = useState<ClientProject[]>([]);
  const [pendingTasks, setPendingTasks] = useState<ClientTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchClientData = useCallback(async () => {
    const userEmail = user?.email;
    if (!userEmail) {
      setError('Unable to identify your account. Please log in again.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // First, find the party linked to the user's email
      const { data: partyData, error: partyError } = await supabase
        .from('parties')
        .select('id, name, primary_email')
        .eq('primary_email', userEmail)
        .limit(1);

      if (partyError) throw partyError;

      const party = partyData?.[0];
      if (!party) {
        // No party found - show helpful message
        setError('No client profile linked to your account. Please contact your advisor to set up your profile.');
        setLoading(false);
        return;
      }

      setClientParty(party);

      // Fetch projects where client is a party
      const { data: partyLinks, error: linksError } = await supabase
        .from('project_parties')
        .select('project_id')
        .eq('party_id', party.id);

      if (linksError) throw linksError;

      if (!partyLinks || partyLinks.length === 0) {
        setProjects([]);
        setPendingTasks([]);
        setLoading(false);
        return;
      }

      const projectIds = partyLinks.map((p: { project_id: string }) => p.project_id);

      // Fetch project details
      const { data: projectsData, error: projectsError } = await supabase
        .from('projects')
        .select('id, project_number, name, status, current_phase, total_phases, tasks_total, tasks_completed, docs_required, docs_received, target_completion_date, engaged_date')
        .in('id', projectIds)
        .in('status', ['engaged', 'in_progress', 'on_hold'])
        .order('created_at', { ascending: false });

      if (projectsError) throw projectsError;
      setProjects(projectsData || []);

      // Fetch client's pending tasks (owner_type = 'client')
      const { data: tasksData, error: tasksError } = await supabase
        .from('tasks')
        .select('id, project_id, title, description, due_date, priority, status')
        .in('project_id', projectIds)
        .eq('owner_type', 'client')
        .in('status', ['not_started', 'in_progress'])
        .order('due_date', { ascending: true })
        .limit(10);

      if (tasksError) throw tasksError;

      // Map project names to tasks
      const tasksWithProjects = (tasksData || []).map((task: ClientTask) => {
        const project = projectsData?.find((p: ClientProject) => p.id === task.project_id);
        return { ...task, project_name: project?.name || 'Unknown Project' };
      });

      setPendingTasks(tasksWithProjects);

    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('Error fetching client data:', err);
      }
      setError('Failed to load your projects. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user?.email]);

  useEffect(() => {
    fetchClientData();
  }, [fetchClientData]);

  // Status badge component
  const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const config: Record<string, { bg: string; text: string; label: string }> = {
      engaged: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Getting Started' },
      in_progress: { bg: 'bg-indigo-100', text: 'text-indigo-800', label: 'In Progress' },
      on_hold: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'On Hold' },
      complete: { bg: 'bg-green-100', text: 'text-green-800', label: 'Complete' },
    };
    const { bg, text, label } = config[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bg} ${text}`}>
        {label}
      </span>
    );
  };

  // Priority indicator
  const PriorityIndicator: React.FC<{ priority: string }> = ({ priority }) => {
    const colors: Record<string, string> = {
      urgent: 'bg-red-500',
      high: 'bg-orange-500',
      normal: 'bg-blue-500',
      low: 'bg-gray-400',
    };
    return <div className={`w-2 h-2 rounded-full ${colors[priority] || colors.normal}`} />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="flex items-center gap-3 text-gray-500">
          <ArrowPathIcon className="h-6 w-6 animate-spin" />
          <span>Loading your projects...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <ExclamationCircleIcon className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-red-900 mb-2">Unable to Load</h3>
          <p className="text-red-700">{error}</p>
          <button
            onClick={fetchClientData}
            className="mt-4 px-4 py-2 bg-red-100 text-red-800 rounded-lg hover:bg-red-200 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Welcome Section */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome back{userProfile?.full_name ? `, ${userProfile.full_name.split(' ')[0]}` : ''}!
        </h1>
        <p className="text-gray-600 mt-1">Track your project progress and complete your tasks below.</p>
      </div>

      {projects.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <FolderOpenIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Projects</h3>
          <p className="text-gray-500 max-w-md mx-auto">
            You don't have any active projects yet. Your advisor will create your project when you begin working together.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content - Projects */}
          <div className="lg:col-span-2 space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Your Projects</h2>
              <span className="text-sm text-gray-500">{projects.length} active</span>
            </div>

            <div className="space-y-4">
              {projects.map((project) => {
                const taskProgress = project.tasks_total > 0
                  ? Math.round((project.tasks_completed / project.tasks_total) * 100)
                  : 0;
                const docProgress = project.docs_required > 0
                  ? Math.round((project.docs_received / project.docs_required) * 100)
                  : 100;

                return (
                  <Link
                    key={project.id}
                    to={`/portal/client/projects/${project.id}`}
                    className="block bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md hover:border-gray-300 transition-all"
                  >
                    <div className="p-6">
                      {/* Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            {project.project_number && (
                              <span className="text-xs font-mono text-gray-500">{project.project_number}</span>
                            )}
                            <StatusBadge status={project.status} />
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900">{project.name}</h3>
                        </div>
                        <ArrowRightIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                      </div>

                      {/* Phase Progress */}
                      <div className="mb-4">
                        <div className="flex items-center justify-between text-sm mb-2">
                          <span className="text-gray-600">Phase {project.current_phase} of {project.total_phases}</span>
                          <span className="font-medium text-gray-900">{taskProgress}% complete</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-600 rounded-full transition-all duration-500"
                            style={{ width: `${taskProgress}%` }}
                          />
                        </div>
                      </div>

                      {/* Stats Row */}
                      <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-100">
                        <div className="flex items-center gap-2">
                          <CheckCircleIcon className="h-4 w-4 text-green-600" />
                          <span className="text-sm text-gray-600">
                            {project.tasks_completed}/{project.tasks_total} tasks
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <DocumentTextIcon className="h-4 w-4 text-purple-600" />
                          <span className="text-sm text-gray-600">
                            {project.docs_received}/{project.docs_required} docs
                          </span>
                        </div>
                        {project.target_completion_date && (
                          <div className="flex items-center gap-2">
                            <CalendarDaysIcon className="h-4 w-4 text-blue-600" />
                            <span className="text-sm text-gray-600">
                              {new Date(project.target_completion_date).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                              })}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Sidebar - What's Next */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">What's Next</h2>
                <ClockIcon className="h-5 w-5 text-gray-400" />
              </div>

              {pendingTasks.length === 0 ? (
                <div className="text-center py-6">
                  <CheckCircleSolidIcon className="h-12 w-12 text-green-500 mx-auto mb-3" />
                  <p className="text-gray-600 font-medium">All caught up!</p>
                  <p className="text-sm text-gray-500 mt-1">No pending tasks for you right now.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingTasks.map((task) => {
                    const isOverdue = task.due_date && new Date(task.due_date) < new Date();

                    return (
                      <Link
                        key={task.id}
                        to={`/portal/client/projects/${task.project_id}`}
                        className="block p-3 rounded-lg border border-gray-100 hover:border-gray-200 hover:bg-gray-50 transition-all"
                      >
                        <div className="flex items-start gap-3">
                          <PriorityIndicator priority={task.priority} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{task.title}</p>
                            <p className="text-xs text-gray-500 truncate mt-0.5">{task.project_name}</p>
                            {task.due_date && (
                              <div className={`flex items-center gap-1 mt-1 ${isOverdue ? 'text-red-600' : 'text-gray-500'}`}>
                                <CalendarDaysIcon className="h-3 w-3" />
                                <span className="text-xs">
                                  {isOverdue ? 'Overdue: ' : ''}
                                  {new Date(task.due_date).toLocaleDateString()}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </Link>
                    );
                  })}

                  {pendingTasks.length >= 10 && (
                    <p className="text-xs text-center text-gray-500 pt-2">
                      Showing first 10 tasks
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Quick Stats */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100 p-6">
              <h3 className="text-sm font-medium text-blue-900 mb-4">Quick Summary</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-blue-700">Active Projects</span>
                  <span className="text-sm font-bold text-blue-900">{projects.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-blue-700">Your Pending Tasks</span>
                  <span className="text-sm font-bold text-blue-900">{pendingTasks.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-blue-700">Documents Needed</span>
                  <span className="text-sm font-bold text-blue-900">
                    {projects.reduce((sum, p) => sum + (p.docs_required - p.docs_received), 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientPortalDashboard;
